import React, { useEffect, useState } from 'react';
import { Alert, Box, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  caregiverExperienceSummaryService,
  CaregiverExperienceSummary,
} from '../../services/caregiverExperienceSummaryService';

// FEAT-067 (MVP-18.5): profile experience summary at top of caregiver profile.

interface ProfileSummaryProps {
  caregiverId: string;
}

const ProfileSummary: React.FC<ProfileSummaryProps> = ({ caregiverId }) => {
  const [summary, setSummary] = useState<CaregiverExperienceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await caregiverExperienceSummaryService.getSummary(caregiverId);
        if (!cancelled) {
          setSummary(data);
        }
      } catch {
        if (!cancelled) {
          setError('We could not load the experience summary right now.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [caregiverId]);

  if (loading) {
    return (
      <Card sx={{ mb: 3 }} data-testid="profile-summary-loading">
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Preparing experience overview…
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error || !summary) {
    return (
      <Alert severity="info" sx={{ mb: 3 }} data-testid="profile-summary-fallback">
        Experience overview will appear here once available.
      </Alert>
    );
  }

  return (
    <Card sx={{ mb: 3 }} data-testid="profile-summary">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AutoAwesomeIcon color="primary" fontSize="small" />
          <Typography variant="h6">Experience Overview</Typography>
        </Box>
        <Typography variant="body1" color="text.primary">
          {summary.summaryText}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default ProfileSummary;
